import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Welcome from "@/pages/Welcome";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { I18nProvider } from "@/i18n/I18nProvider";
import { TaxonomyProvider } from "@/data/TaxonomyProvider";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { takePendingInvite } from "@/lib/pendingInvite";
import { resetZoom } from "@/lib/zoomReset";
import { useAuthStore } from "@/services/authStore";

// Only the consumer path (Welcome + Home) ships in the entry chunk — every
// other page code-splits so the first paint of "/" isn't paying for the
// operator panel, auth UI, or the marketing page. MapView lazy-loads deeper
// down (inside Home) for the same reason.
const Landing = lazy(() => import("@/pages/landing/Landing"));
// Sales demo for venue managers: the real feed on a bundled Cluj snapshot with
// simulated capacity (docs/features/showcase.md). Ships to production, noindex.
const Showcase = lazy(() => import("@/pages/showcase/Showcase"));
const Legal = lazy(() => import("@/pages/Legal"));
const GoLive = lazy(() => import("@/pages/GoLive"));
const FriendInvite = lazy(() => import("@/pages/FriendInvite"));
const Admin = lazy(() => import("@/pages/Admin"));
const PosSimulator = lazy(() => import("@/pages/PosSimulator"));
const SignIn = lazy(() => import("@/pages/SignIn"));
const OperatorIndex = lazy(() => import("@/pages/operator/OperatorIndex"));
const OperatorDashboard = lazy(() => import("@/pages/operator/OperatorDashboard"));
const OperatorEditor = lazy(() => import("@/pages/operator/OperatorEditor"));
const OperatorLinks = lazy(() => import("@/pages/operator/OperatorLinks"));
const ManagerLogins = lazy(() => import("@/pages/operator/ManagerLogins"));
const OperatorBonus = lazy(() => import("@/pages/operator/OperatorBonus"));
const OperatorReports = lazy(() => import("@/pages/operator/OperatorReports"));
const OperatorLeads = lazy(() => import("@/pages/operator/OperatorLeads"));
const ManagerEditor = lazy(() => import("@/pages/manager/ManagerEditor"));

/**
 * The investor deck is a self-contained static page (`public/deck.html`)
 * served outside the SPA bundle; this route exists so `/deck` works from
 * router links and hard refreshes alike.
 */
function DeckRedirect() {
  useEffect(() => {
    window.location.replace("/deck.html");
  }, []);
  return null;
}

/**
 * Boot Supabase Auth state on app mount so every route guard already has a
 * resolved status by the time it renders.
 */
function AuthBoot() {
  const initialize = useAuthStore((s) => s.initialize);
  useEffect(() => {
    void initialize();
  }, [initialize]);
  return null;
}

/**
 * A friend invite opened while signed out parks its token on the device
 * (`src/lib/pendingInvite.ts`). Google and email-confirm sign-ins land on
 * /app, so once a session is proven we send the visitor back to the invite.
 * Staff surfaces and the sales demo are left alone.
 */
function PendingInviteReturn() {
  const status = useAuthStore((s) => s.status);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (status !== "signed_in") return;
    if (/^\/(f|admin|manage|signin|showcase-cluj)(\/|$)/.test(pathname)) return;
    const token = takePendingInvite();
    if (token) navigate(`/f/${token}`, { replace: true });
  }, [status, pathname, navigate]);
  return null;
}

/** A new page starts at 100% even if the last one was left pinch-zoomed. */
function ZoomResetOnNavigate() {
  const { pathname } = useLocation();
  useEffect(resetZoom, [pathname]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
    <I18nProvider>
      <TaxonomyProvider>
        <BrowserRouter>
          <AuthBoot />
          <PendingInviteReturn />
          <ZoomResetOnNavigate />
          <Suspense fallback={null}>
          <Routes>
          {/* Public surface */}
          <Route path="/" element={<Welcome />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/bonus-rules" element={<Legal />} />
          <Route path="/deck" element={<DeckRedirect />} />
          <Route path="/app" element={<Home />} />
          <Route path="/showcase-cluj/*" element={<Showcase />} />
          {/* Path-based city pages (/cluj, /bucuresti, …) — the canonical,
              indexable city URL. React Router v6 ranks static segments above
              this dynamic one, so it can't shadow /landing, /terms, /admin, etc;
              an unknown slug resolves to no city → Home redirects to the gate.
              Prerendered content lives at these paths and the app takes over. */}
          <Route path="/:citySlug" element={<Home />} />
          <Route path="/go/:key" element={<GoLive />} />
          <Route path="/f/:token" element={<FriendInvite />} />
          <Route path="/signin" element={<SignIn />} />

          {/* Restaurant manager — single scoped venue editor. The venue comes
              from the auth profile (restaurant_id), never the URL. */}
          <Route
            path="/manage"
            element={
              <RequireAuth roles={["manager"]}>
                <ManagerEditor />
              </RequireAuth>
            }
          />

          {/* Entry point — redirects each role to its single workspace. */}
          <Route
            path="/admin"
            element={
              <RequireAuth roles={["operator", "super_admin"]}>
                <Admin />
              </RequireAuth>
            }
          />

          {/* Super-admin only — POS pipeline */}
          <Route
            path="/admin/pos-simulator"
            element={
              <RequireAuth roles={["super_admin"]}>
                <PosSimulator />
              </RequireAuth>
            }
          />

          {/* Operator only — venue manager (CRUD) */}
          <Route
            path="/admin/operator"
            element={
              <RequireAuth roles={["operator"]}>
                <OperatorIndex />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/new"
            element={
              <RequireAuth roles={["operator"]}>
                <OperatorEditor />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/links"
            element={
              <RequireAuth roles={["operator"]}>
                <OperatorLinks />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/managers"
            element={
              <RequireAuth roles={["operator", "super_admin"]}>
                <ManagerLogins />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/bonus"
            element={
              <RequireAuth roles={["operator", "super_admin"]}>
                <OperatorBonus />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/reports"
            element={
              <RequireAuth roles={["operator", "super_admin"]}>
                <OperatorReports />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/leads"
            element={
              <RequireAuth roles={["operator", "super_admin"]}>
                <OperatorLeads />
              </RequireAuth>
            }
          />
          {/* Static path MUST precede /:id so the editor's dynamic route
              doesn't capture "dashboard". */}
          <Route
            path="/admin/operator/dashboard"
            element={
              <RequireAuth roles={["operator", "super_admin"]}>
                <OperatorDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/operator/:id"
            element={
              <RequireAuth roles={["operator"]}>
                <OperatorEditor />
              </RequireAuth>
            }
          />

          <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TaxonomyProvider>
    </I18nProvider>
    </ErrorBoundary>
  );
}
